import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { PlatformKind, PlatformMarkSettings, performanceApi } from '../../services/performanceApi';
import { PlatformRouteDialog } from '../platform/PlatformRouteCard';

type WaitOption = 'search' | 'text' | 'vision' | 'kitchen' | 'custom' | 'none';

// The platform's Go deadlines (LLMAPI_*_TIMEOUT, platformKitchenTimeout), as in PLATFORM_ROUTES on the backend.
const WAIT_PRESETS: { value: Exclude<WaitOption, 'custom' | 'none'>; label: string; seconds: number }[] = [
  { value: 'search', label: 'Search: 20s', seconds: 20 },
  { value: 'text', label: 'Text: 45s', seconds: 45 },
  { value: 'vision', label: 'Vision: 55s', seconds: 55 },
  { value: 'kitchen', label: 'Kitchen and health: 85s', seconds: 85 },
];

const KIND_OPTIONS: { value: PlatformKind; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'vision', label: 'Vision' },
  { value: 'search', label: 'Search' },
  { value: 'kitchen', label: 'Kitchen and health' },
  { value: 'other', label: 'Other' },
];

const waitFor = (seconds: number | null | undefined): WaitOption => {
  if (seconds === null || seconds === undefined) return 'none';
  return WAIT_PRESETS.find(p => p.seconds === seconds)?.value ?? 'custom';
};

interface Props {
  open: boolean;
  onClose: () => void;
  functionId: string;
  functionName: string;
  mark: PlatformMarkSettings | null;
  onSaved: () => void;
}

export const PlatformFunctionDialog: React.FC<Props> = ({ open, onClose, functionId, functionName, mark, onSaved }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => performanceApi(getIdToken), [getIdToken]);
  const [tool, setTool] = useState(mark?.tool ?? '');
  const [wait, setWait] = useState<WaitOption>(mark ? waitFor(mark.go_timeout_s) : 'text');
  const [customSeconds, setCustomSeconds] = useState(mark && waitFor(mark.go_timeout_s) === 'custom' ? String(mark.go_timeout_s) : '');
  const [kind, setKind] = useState<PlatformKind>(mark?.kind ?? 'text');
  const [kindTouched, setKindTouched] = useState(!!mark);
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [routeOpen, setRouteOpen] = useState(false);
  const [routeChanged, setRouteChanged] = useState(false);

  const custom = Number(customSeconds);
  const seconds = wait === 'custom' ? custom : WAIT_PRESETS.find(p => p.value === wait)?.seconds ?? null;
  const secondsInvalid = wait === 'custom' && (!customSeconds.trim() || !Number.isFinite(custom) || custom < 1 || custom > 600);
  const toolInvalid = !tool.trim() || tool.trim().length > 255;

  const changeWait = (value: WaitOption) => {
    setWait(value);
    if (!kindTouched && value !== 'custom' && value !== 'none') setKind(value);
  };

  const run = async (action: 'save' | 'remove') => {
    setBusy(action);
    setError(null);
    try {
      if (action === 'save') await api.markPlatformFunction(functionId, { tool: tool.trim(), go_timeout_s: seconds, kind });
      else await api.unmarkPlatformFunction(functionId);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mark ? 'Platform function settings' : 'Mark as platform function'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Treat <b>{functionName}</b> (all versions) as a function the Passio platform calls: it is listed on Platform Routes, its time limit is checked
          against the platform&apos;s wait, and slow runs raise platform alerts.           This only changes EasyAI monitoring; the platform still has to be set up to
          call this function, or serve it as a dynamic route.
        </Typography>
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={() => setRouteOpen(true)} disabled={!!busy}>
              {mark?.slug ? 'Route settings' : 'Serve as a route'}
            </Button>
          }
        >
          {mark?.slug
            ? `Served by the platform at /napi/tools/${mark.slug} (${mark.status ?? 'off'}).`
            : 'Dynamic route: the platform serves this function at /napi/tools/<name> with no platform release, and the platform defaults are applied.'}
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="Passio tool name"
            size="small"
            value={tool}
            onChange={e => setTool(e.target.value)}
            placeholder="e.g. GenerateSearchTerms"
            helperText={mark?.slug ? 'Set from the route name.' : "The tool name in the platform's LLM service."}
            inputProps={{ maxLength: 255 }}
            disabled={!!mark?.slug}
            required
            autoFocus
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Platform wait"
              size="small"
              value={wait}
              onChange={e => changeWait(e.target.value as WaitOption)}
              helperText="How long the platform waits for EasyAI before giving up."
              sx={{ flex: 1 }}
            >
              {WAIT_PRESETS.map(p => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom seconds</MenuItem>
              <MenuItem value="none">Not set</MenuItem>
            </TextField>
            {wait === 'custom' && (
              <TextField
                label="Seconds"
                size="small"
                type="number"
                value={customSeconds}
                onChange={e => setCustomSeconds(e.target.value)}
                error={secondsInvalid && customSeconds !== ''}
                helperText="1 to 600"
                inputProps={{ min: 1, max: 600, step: 1 }}
                sx={{ width: 120 }}
              />
            )}
          </Box>
          {wait === 'none' && (
            <Typography variant="caption" color="text.secondary">
              Without a wait there is no headroom check and no near-timeout warning.
            </Typography>
          )}
          <TextField
            select
            label="Kind"
            size="small"
            value={kind}
            onChange={e => {
              setKind(e.target.value as PlatformKind);
              setKindTouched(true);
            }}
          >
            {KIND_OPTIONS.map(k => (
              <MenuItem key={k.value} value={k.value}>
                {k.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        {mark && (
          <Button color="error" onClick={() => run('remove')} disabled={!!busy} sx={{ mr: 'auto' }}>
            {busy === 'remove' ? <CircularProgress size={18} /> : 'Remove'}
          </Button>
        )}
        <Button onClick={onClose} disabled={!!busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => run('save')} disabled={!!busy || toolInvalid || secondsInvalid}>
          {busy === 'save' ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
      <PlatformRouteDialog
        open={routeOpen}
        functionId={functionId}
        title={`Platform route: ${functionName}`}
        onClose={() => {
          setRouteOpen(false);
          if (routeChanged) {
            onSaved();
            onClose();
          }
        }}
        onChanged={() => setRouteChanged(true)}
      />
    </Dialog>
  );
};

export default PlatformFunctionDialog;
